import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
	ChangeDetectorRef,
	Component,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { firstValueFrom, take } from 'rxjs';

import { AlertService } from '@valk-nx/components/ui-alert/src/lib/services/alert.service';
import { fallbackLanguage, HOTEL_SLUG } from '@valk-nx/core/lib/core';
import { RecaptchaRequest } from '@valk-nx/core/lib/interfaces/recaptcha.interface';
import { SharedFormlyModule } from '@valk-nx/formly/lib/formly.module';
import { ReCaptchaService } from '@valk-nx/services/recaptcha/src/lib/recaptcha.service';
import { HeadingGroupComponent } from '@valk-nx/storyblok/components/heading-group/src/lib/heading-group';
import { alertErrorSettings } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { PromotionCodeInterface } from '@valk-nx/storyblok-types/src/lib/types/content.interface';

import { promoFormFields, promoFormGroup } from './form';

interface PromotionCodeFormModel {
	promoCode: string;
	token?: string;
}

export type PromotionCodeRequest = RecaptchaRequest & PromotionCodeFormModel;

@Component({
	selector: 'sb-promotion-code-promo-form',
	templateUrl: './promotion-code-promo-form.html',
	imports: [
		CommonModule,
		HeadingGroupComponent,
		ReactiveFormsModule,
		SharedFormlyModule,
		TranslatePipe,
	],
})
export class PromotionCodePromoFormComponent {
	private readonly alertService = inject(AlertService);
	private readonly contentService = inject(ContentService);
	private readonly recaptchaService = inject(ReCaptchaService);
	private readonly translate = inject(TranslateService);
	private readonly hotelSlug = inject(HOTEL_SLUG);

	readonly cd = inject(ChangeDetectorRef);

	promotionCode = input<string>('');

	promotionCodeData = output<PromotionCodeInterface>();

	recaptchaStatus = signal<'recaptcha-forbidden' | 'recaptcha-awaiting'>(
		'recaptcha-awaiting',
	);
	requestSubmitting = signal<boolean>(false);

	formModel = signal<PromotionCodeFormModel>({ promoCode: '' });
	formFields = promoFormFields;
	formGroup = promoFormGroup;

	currentLanguage: string = fallbackLanguage;

	constructor() {
		this.currentLanguage = this.translate.getCurrentLang();

		dayjs.extend(isSameOrBefore);

		this.formFields = [
			...this.formFields,
			this.recaptchaService.addV2RecaptchaFormField(
				toObservable(this.recaptchaStatus),
				'token',
			),
		];

		effect(() => {
			const code = this.promotionCode();
			if (code) {
				this.setPromoCode(code);
			}
		});
	}

	onSubmit(): void {
		if (this.formGroup.valid) {
			this.prepareRequestData();
		}
	}

	setPromoCode(code: string): void {
		this.formGroup.patchValue({
			promoCode: code,
		});
		this.formModel().promoCode = code;
	}

	async prepareRequestData() {
		this.requestSubmitting.set(true);

		const request = await firstValueFrom(
			this.recaptchaService
				.addRecaptchaToken(this.formModel(), 'promotionCode', 'token')
				.pipe(take(1)),
		);

		await this.submitPromotionCode(request);
	}

	async submitPromotionCode(formData: PromotionCodeRequest): Promise<void> {
		const uppercasedPromoCode = formData.promoCode.toUpperCase();
		const hotelSlugs = this.hotelSlug ? [this.hotelSlug] : [];

		return this.contentService
			.submitPromotionCode(
				uppercasedPromoCode,
				this.currentLanguage,
				hotelSlugs,
				formData.token,
				formData.recaptchaVersion,
			)
			.then((response) => {
				const control = this.formGroup.controls.promoCode;
				const promoIsActive =
					response.visible?.to &&
					dayjs().isSameOrBefore(dayjs(response.visible.to));

				if (!promoIsActive) {
					control.setErrors({
						inactive: true,
					});
				} else if (response?.hotels.length === 0) {
					control.setErrors({
						notFound: true,
					});
				} else {
					control.setErrors(null);
				}

				control.markAsTouched();

				if (response?.hotels.length >= 1 && promoIsActive) {
					this.promotionCodeData.emit(response);
				}
			})
			.catch((error: HttpErrorResponse) => {
				if (error.status === 403) {
					this.recaptchaStatus.set('recaptcha-forbidden');
				} else {
					this.alertService.error({
						...alertErrorSettings,
						content: this.translate.instant('global.service.error'),
					});
				}
			})
			.finally(() => {
				this.requestSubmitting.set(false);
				this.cd.markForCheck();
			});
	}
}
